import React, { useCallback, useEffect, useRef, useState } from "react";
import { Amplify, API } from "aws-amplify";
import {
  Authenticator,
  TextField,
  Alert,
  Divider,
  Badge,
} from "@aws-amplify/ui-react";
import { Box, Flex, Card } from "theme-ui";
import {
  Heading,
  Button as LumosButton,
  useNotify,
} from "@loanpal/lumos__core";
import NavBar from "../../shared/nav-bar";
import { formFields, components } from "../../utils/authentication";
import Loading from "../../utils/loading";
import { getTokenWithRetry } from "../../utils/api";
import awsExports from "../../aws-exports";
import "@aws-amplify/ui-react/styles.css";

Amplify.configure(awsExports);

/** Parent integrator for QuickLink connected-org checks. */
const GOODLINK_PARENT = "GoodLink";

const STEPS = [
  "Organization Look up",
  "Connected org",
  "Request details",
  "Create QuickLink",
];

function StepBubble({ stepNumber, title }) {
  return (
    <Flex sx={{ alignItems: "center", gap: 2 }}>
      <Flex
        sx={{
          width: 28,
          height: 28,
          borderRadius: "50%",
          alignItems: "center",
          justifyContent: "center",
          bg: "#472BA4",
          color: "white",
          fontSize: 1,
          fontWeight: 700,
          flexShrink: 0,
        }}
        aria-label={`Step ${stepNumber} of ${STEPS.length}`}
      >
        {stepNumber}
      </Flex>
      <Box sx={{ fontSize: 1, fontWeight: 700, color: "#323232" }}>{title}</Box>
    </Flex>
  );
}

function InfoRow({ label, value }) {
  if (value == null || value === "") return null;
  return (
    <Flex
      sx={{
        gap: 3,
        py: 2,
        borderBottom: "1px solid #eee",
        alignItems: "flex-start",
      }}
    >
      <Box sx={{ width: 180, flexShrink: 0, fontWeight: 600, fontSize: 1, color: "#5c5c5c" }}>
        {label}
      </Box>
      <Box sx={{ fontSize: 1, wordBreak: "break-word", flex: 1 }}>
        {typeof value === "string" && value.startsWith("http") ? (
          <a href={value} target="_blank" rel="noopener noreferrer">
            {value}
          </a>
        ) : (
          String(value)
        )}
      </Box>
    </Flex>
  );
}

function ExistingList({ title, items, linkPrefix }) {
  if (!items?.length) return null;
  return (
    <Box sx={{ mt: 3 }}>
      <Heading variant="h6" as="h3" sx={{ mb: 2, fontSize: 2 }}>
        {title}
      </Heading>
      <Box as="ul" sx={{ m: 0, pl: 3 }}>
        {items.map((item) => (
          <Box as="li" key={item.id} sx={{ mb: 1, fontSize: 1 }}>
            <a href={`${linkPrefix}${item.id}`}>
              {item.accountName || item.partnerName || item.id}
            </a>
            {" · "}
            <Badge size="small">{item.status || "—"}</Badge>
            {item.category ? ` · ${item.category}` : null}
            {item.clientId ? ` · ${item.clientId}` : null}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

const CreateQuicklink = () => {
  const { addSnack } = useNotify();
  const [activeStep, setActiveStep] = useState(0);
  const [salesforceId, setSalesforceId] = useState("");
  const [loading, setLoading] = useState(false);
  const [lookupError, setLookupError] = useState("");
  const [lookupResult, setLookupResult] = useState(null);

  const [connectedOrgStatus, setConnectedOrgStatus] = useState("idle"); // idle|checking|ok|fail
  const [connectedOrgError, setConnectedOrgError] = useState("");
  const connectedOrgCheckKeyRef = useRef("");

  const organization = lookupResult?.organization || null;
  const existingRequests = lookupResult?.existingRequests || [];
  const existingSharedApps = lookupResult?.existingSharedApps || [];
  const alreadyExists = lookupResult?.alreadyExists || {
    request: false,
    sharedApp: false,
  };

  const organizationId = String(organization?.organizationId || "").trim();

  const runLookup = async () => {
    const id = String(salesforceId || "").trim();
    if (!id) {
      addSnack({ variant: "danger", message: "Enter a Salesforce Account Id." });
      return;
    }
    setLoading(true);
    setLookupError("");
    setLookupResult(null);
    setConnectedOrgStatus("idle");
    setConnectedOrgError("");
    connectedOrgCheckKeyRef.current = "";
    try {
      const response = await API.post("developerApi", "/create-quicklink/lookup", {
        body: { salesforceId: id },
      });
      setLookupResult(response);
      if (response?.alreadyExists?.request || response?.alreadyExists?.sharedApp) {
        addSnack({
          variant: "warning",
          message: "This Salesforce Id already has a Request and/or QuickLink.",
        });
      } else {
        addSnack({ variant: "success", message: "Organization found." });
      }
    } catch (err) {
      console.log("create-quicklink lookup error", err);
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        "Failed to look up Salesforce organization.";
      setLookupError(msg);
      const body = err?.response?.data;
      if (body?.existingRequests || body?.salesforceId) {
        setLookupResult({
          salesforceId: body.salesforceId || id,
          organization: null,
          existingRequests: body.existingRequests || [],
          existingSharedApps: body.existingSharedApps || [],
          alreadyExists: {
            request: (body.existingRequests || []).length > 0,
            sharedApp: (body.existingSharedApps || []).length > 0,
          },
        });
      }
      addSnack({ variant: "danger", message: msg });
    } finally {
      setLoading(false);
    }
  };

  const verifyConnectedOrg = useCallback(async () => {
    if (!organizationId) {
      setConnectedOrgStatus("fail");
      setConnectedOrgError(
        "Organization Id is missing from the lookup. Cannot verify Connected Orgs."
      );
      return;
    }
    const checkKey = `${GOODLINK_PARENT}|${organizationId}`;
    connectedOrgCheckKeyRef.current = checkKey;
    setConnectedOrgStatus("checking");
    setConnectedOrgError("");
    setLoading(true);
    try {
      const result = await getTokenWithRetry(GOODLINK_PARENT, organizationId);
      if (connectedOrgCheckKeyRef.current !== checkKey) return;
      if (result?.token) {
        setConnectedOrgStatus("ok");
        addSnack({
          variant: "success",
          message: "Organization is connected to GoodLink.",
        });
        return;
      }
      setConnectedOrgStatus("fail");
      setConnectedOrgError(
        `Could not retrieve an elevated token. Confirm this organization (${organizationId}) is connected under parent "${GOODLINK_PARENT}".`
      );
      addSnack({
        variant: "danger",
        message: "Connected org check failed — elevated token not available.",
      });
    } catch (err) {
      if (connectedOrgCheckKeyRef.current !== checkKey) return;
      console.log("create-quicklink connected org check error", err);
      setConnectedOrgStatus("fail");
      setConnectedOrgError(
        err?.message ||
          `Connected org check failed for parent "${GOODLINK_PARENT}".`
      );
      addSnack({ variant: "danger", message: "Connected org check failed." });
    } finally {
      setLoading(false);
    }
  }, [organizationId, addSnack]);

  // Auto-run connected-org check when entering step 2.
  useEffect(() => {
    if (activeStep !== 1) return;
    if (!organizationId) {
      setConnectedOrgStatus("fail");
      setConnectedOrgError(
        "Organization Id is missing from the lookup. Cannot verify Connected Orgs."
      );
      return;
    }
    if (connectedOrgStatus === "ok" || connectedOrgStatus === "checking") return;
    verifyConnectedOrg();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep, organizationId]);

  const goToConnectedOrgStep = () => {
    setConnectedOrgStatus("idle");
    setConnectedOrgError("");
    connectedOrgCheckKeyRef.current = "";
    setActiveStep(1);
  };

  return (
    <Authenticator formFields={formFields} components={components} hideSignUp>
      {({ signOut }) => (
        <>
          {loading ? <Loading /> : null}
          <NavBar>
            <LumosButton onClick={signOut} style={styles.signOut}>
              Sign out
            </LumosButton>
          </NavBar>

          <Box sx={styles.page}>
            <Heading variant="h4" sx={{ mb: 2 }}>
              Create QuickLink
            </Heading>
            <Box sx={{ color: "#5c5c5c", fontSize: 1, mb: 4, maxWidth: 640 }}>
              Step-by-step wizard to create a Request, then a SharedApp / QuickLink.
            </Box>

            <Box sx={{ mb: 3 }}>
              <StepBubble
                stepNumber={activeStep + 1}
                title={STEPS[activeStep]}
              />
            </Box>

            {activeStep === 0 ? (
              <Card sx={styles.card}>
                <Heading variant="h5" as="h2" sx={{ mb: 2 }}>
                  Step 1 — Organization Look up
                </Heading>
                <Box sx={{ color: "#5c5c5c", fontSize: 1, mb: 3 }}>
                  Enter the Salesforce Account Id. We look up organization details in
                  Databricks and check whether a Request or QuickLink already exists.
                </Box>

                <Flex
                  sx={{
                    gap: 3,
                    alignItems: "flex-end",
                    flexWrap: "wrap",
                    mb: 3,
                  }}
                >
                  <Box sx={{ flex: "1 1 280px", maxWidth: 420 }}>
                    <TextField
                      label="Salesforce Account Id"
                      size="small"
                      value={salesforceId}
                      onChange={(e) => setSalesforceId(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") runLookup();
                      }}
                      placeholder="001…"
                    />
                  </Box>
                  <LumosButton
                    type="button"
                    onClick={runLookup}
                    disabled={loading}
                  >
                    {loading ? "Looking up…" : "Look up organization"}
                  </LumosButton>
                </Flex>

                {lookupError ? (
                  <Alert variation="error" marginBottom="1rem">
                    {lookupError}
                  </Alert>
                ) : null}

                {alreadyExists.request || alreadyExists.sharedApp ? (
                  <Alert variation="warning" marginBottom="1rem">
                    {alreadyExists.request && alreadyExists.sharedApp
                      ? "A Request and a QuickLink already exist for this Salesforce Id / organization."
                      : alreadyExists.request
                        ? "A Request already exists for this Salesforce Id."
                        : "A QuickLink (SharedApp) already exists for this organization / client."}
                  </Alert>
                ) : null}

                {organization ? (
                  <Box sx={{ mt: 2 }}>
                    <Heading variant="h6" as="h3" sx={{ mb: 2, fontSize: 2 }}>
                      Organization details
                    </Heading>
                    <Box
                      sx={{
                        border: "1px solid #eee",
                        borderRadius: 8,
                        px: 3,
                        bg: "#fafafa",
                      }}
                    >
                      <InfoRow label="Account name" value={organization.accountName} />
                      <InfoRow label="Client Id" value={organization.clientId} />
                      <InfoRow
                        label="Organization Id"
                        value={organization.organizationId}
                      />
                      <InfoRow
                        label="Account owner email"
                        value={organization.accountOwnerEmail}
                      />
                      <InfoRow
                        label="Partner email"
                        value={organization.partnerEmail}
                      />
                      <InfoRow
                        label="Submitting user email"
                        value={organization.submittingUserEmail}
                      />
                      <InfoRow label="Salesforce" value={organization.salesforce} />
                      <InfoRow
                        label="Salesforce Id"
                        value={organization.salesforceId || salesforceId}
                      />
                    </Box>

                    <ExistingList
                      title="Existing requests"
                      items={existingRequests}
                      linkPrefix="/request/"
                    />
                    <ExistingList
                      title="Existing QuickLinks"
                      items={existingSharedApps}
                      linkPrefix="/edit/"
                    />

                    <Divider marginTop="1.5rem" marginBottom="1rem" />
                    <Flex sx={{ justifyContent: "flex-end", gap: 2 }}>
                      <LumosButton
                        type="button"
                        variant="primary"
                        disabled={!organization || !organizationId}
                        onClick={goToConnectedOrgStep}
                      >
                        Continue
                      </LumosButton>
                    </Flex>
                  </Box>
                ) : null}
              </Card>
            ) : null}

            {activeStep === 1 ? (
              <Card sx={styles.card}>
                <Heading variant="h5" as="h2" sx={{ mb: 2 }}>
                  Step 2 — Connected org
                </Heading>
                <Box sx={{ color: "#5c5c5c", fontSize: 1, mb: 3 }}>
                  Verify this organization can obtain an elevated access token through
                  Connected Orgs under the <strong>{GOODLINK_PARENT}</strong> parent.
                </Box>

                {organization ? (
                  <Box
                    sx={{
                      border: "1px solid #eee",
                      borderRadius: 8,
                      px: 3,
                      bg: "#fafafa",
                      mb: 3,
                    }}
                  >
                    <InfoRow label="Account name" value={organization.accountName} />
                    <InfoRow label="Client Id" value={organization.clientId} />
                    <InfoRow label="Parent org" value={GOODLINK_PARENT} />
                    <InfoRow label="Organization Id" value={organizationId} />
                  </Box>
                ) : null}

                {connectedOrgStatus === "checking" ? (
                  <Alert variation="info" marginBottom="1rem">
                    Checking Connected Orgs elevated token…
                  </Alert>
                ) : null}

                {connectedOrgStatus === "ok" ? (
                  <Alert variation="success" marginBottom="1rem">
                    Connected org check passed. Elevated token retrieved successfully.
                  </Alert>
                ) : null}

                {connectedOrgStatus === "fail" ? (
                  <Alert variation="error" marginBottom="1rem">
                    {connectedOrgError ||
                      "Connected org check failed. Fix the GoodLink connection, then retry."}
                  </Alert>
                ) : null}

                <Divider marginTop="0.5rem" marginBottom="1rem" />
                <Flex sx={{ justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
                  <LumosButton
                    type="button"
                    variant="secondary"
                    onClick={() => setActiveStep(0)}
                  >
                    Back
                  </LumosButton>
                  <Flex sx={{ gap: 2 }}>
                    {connectedOrgStatus === "fail" || connectedOrgStatus === "idle" ? (
                      <LumosButton
                        type="button"
                        variant="secondary"
                        disabled={loading || !organizationId}
                        onClick={verifyConnectedOrg}
                      >
                        {loading ? "Checking…" : "Retry check"}
                      </LumosButton>
                    ) : null}
                    <LumosButton
                      type="button"
                      variant="primary"
                      disabled={connectedOrgStatus !== "ok"}
                      onClick={() => {
                        setActiveStep(2);
                        addSnack({
                          variant: "info",
                          message:
                            "Next steps (request details + QuickLink create) coming next.",
                        });
                      }}
                    >
                      Continue
                    </LumosButton>
                  </Flex>
                </Flex>
              </Card>
            ) : null}

            {activeStep > 1 ? (
              <Card sx={styles.card}>
                <Heading variant="h5" as="h2" sx={{ mb: 2 }}>
                  Step {activeStep + 1} — {STEPS[activeStep]}
                </Heading>
                <Box sx={{ color: "#5c5c5c", fontSize: 1, mb: 3 }}>
                  This step will be wired next. Organization and Connected Orgs check
                  from earlier steps are ready.
                </Box>
                {organization ? (
                  <Box sx={{ mb: 3, fontSize: 1 }}>
                    <strong>{organization.accountName}</strong>
                    {organization.clientId ? ` · ${organization.clientId}` : ""}
                    {connectedOrgStatus === "ok" ? " · Connected to GoodLink" : ""}
                  </Box>
                ) : null}
                <LumosButton
                  type="button"
                  variant="secondary"
                  onClick={() => setActiveStep(1)}
                >
                  Back to Connected org
                </LumosButton>
              </Card>
            ) : null}
          </Box>
        </>
      )}
    </Authenticator>
  );
};

const styles = {
  page: {
    maxWidth: 900,
    mx: "auto",
    px: [3, 4],
    py: 4,
  },
  card: {
    p: [3, 4],
    borderRadius: 8,
    border: "1px solid #e5e5e5",
    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
    bg: "white",
  },
  signOut: {
    backgroundColor: "transparent",
    color: "white",
    border: "none",
  },
};

export default CreateQuicklink;
